import React, {useCallback, useEffect, useMemo} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  adminSignupsApiSelector,
  adminSignupsPageSelector,
  adminSignupsSelector,
  adminSignupsTotalSelector,
} from '../store/selectors/admin.selectors';
import {fetchAdminSignups} from '../store/slices/adminThunks';
import {ADMIN_SIGNUPS_PAGE_SIZE, SignupStatus} from '../domain/admin';
import AdminSignupsTable from './admin/AdminSignupsTable';
import {Pagination, Panel} from './admin/AdminPrimitives';

/**
 * The signup queue.
 *
 * **Defaults to `failed`** rather than to everything, because that filter is the reason the screen
 * exists: those rows are paying customers who cannot sign in. The unfiltered list is mostly
 * `expired` — abandoned checkouts that cost nobody anything — and putting that in front of an
 * operator by default buries the four rows that matter.
 *
 * The filter lives in the URL so the dashboard's alert can link straight into it.
 */

const STATUSES: Array<{key: SignupStatus | 'all'; label: string; hint: string}> = [
  {key: 'failed', label: 'Failed', hint: 'Paid, no account. Start here.'},
  {key: 'pending', label: 'Pending', hint: 'Started, not yet provisioned.'},
  {key: 'provisioned', label: 'Provisioned', hint: 'Account exists, link emailed.'},
  {key: 'expired', label: 'Expired', hint: 'Token expired before checkout. Nothing charged.'},
  {key: 'all', label: 'All', hint: 'Every signup record.'},
];

const isSignupStatus = (value: string | null): value is SignupStatus =>
  value === 'pending' || value === 'provisioned' || value === 'expired' || value === 'failed';

const AdminSignupsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const signups = useAppSelector(adminSignupsSelector);
  const total = useAppSelector(adminSignupsTotalSelector);
  const page = useAppSelector(adminSignupsPageSelector);
  const api = useAppSelector(adminSignupsApiSelector);

  const statusParam = searchParams.get('status');
  // No parameter at all means "the default", and the default is failed. `?status=all` is how you
  // deliberately ask for everything — it is not the same thing as arriving with no filter.
  const status: SignupStatus | 'all' = statusParam === 'all'
    ? 'all'
    : isSignupStatus(statusParam) ? statusParam : 'failed';
  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0) || 0);

  const query = useMemo(
    () => ({
      status: status === 'all' ? undefined : status,
      limit: ADMIN_SIGNUPS_PAGE_SIZE,
      offset,
    }),
    [status, offset]
  );

  useEffect(() => {
    dispatch(fetchAdminSignups(query));
  }, [dispatch, query]);

  const setParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(patch).forEach(([key, value]) => {
            if (value === undefined) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  return (
    <Panel
      title="Signups"
      subtitle="Provisioning is retried by cron for up to five minutes; anything still failed has had more than one attempt."
      padded={false}
    >
      <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-800 px-4 py-3">
        {STATUSES.map(entry => (
          <button
            key={entry.key}
            type="button"
            title={entry.hint}
            onClick={() => setParams({status: entry.key, offset: undefined})}
            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
              status === entry.key
                ? 'bg-gray-800 text-white'
                : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
            }`}
          >
            {entry.label}
          </button>
        ))}
        <span className="ml-auto text-[11px] tabular-nums text-gray-600">
          {api.status === 'pending' ? 'Loading…' : `${total.toLocaleString()} matching`}
        </span>
      </div>

      <AdminSignupsTable
        signups={signups}
        isLoading={api.status === 'pending'}
        error={api.status === 'rejected' ? api.error : null}
        onRetry={() => dispatch(fetchAdminSignups(query))}
        status={status}
      />

      <Pagination
        total={total}
        limit={page?.limit || ADMIN_SIGNUPS_PAGE_SIZE}
        offset={offset}
        onOffsetChange={next => setParams({offset: String(next)})}
        noun="signups"
        isLoading={api.status === 'pending'}
      />
    </Panel>
  );
};

export default AdminSignupsContainer;
